import { z } from 'zod/v4';
import {
  QuestionnaireBuilderQuestionSchema,
  QuestionnaireBuilderSectionSchema,
} from './questionnaire-builder.js';

export const GetQuestionnaireRequestDetailParamsSchema = z.object({
  requestId: z.string().min(1),
});

export type GetQuestionnaireRequestDetailParams = z.infer<
  typeof GetQuestionnaireRequestDetailParamsSchema
>;

/**
 * Answer statistics for one answer request.
 *
 * A **closed** object, and a different key set from
 * `QuestionnaireBuilderStatisticsSchema`: the legacy response schema declared
 * `additionalProperties: false` over exactly these twelve keys, so the stored
 * blob was truncated to them on the way out. The questionnaire-level blob is
 * counted per request (`totalOpenRequests`, `totalCompaniesInvited`, …) and has
 * no overlap with the three keys unique to this one — `totalResponses`,
 * `totalComments` and `responsesPercentage`.
 *
 * Every key is nullable and none carries a range constraint, for the reason
 * spelled out on the builder statistics: this schema serialises a stored blob
 * rather than validating an input, and the legacy `minimum`/`maximum` were never
 * enforced (fast-json-stringify only coerces types on the way out). Porting them
 * would turn documentation into a 500 the first time a stored percentage landed
 * outside 0–100.
 */
const statistic = () => z.number().nullable().optional();

export const QuestionnaireRequestAnswerStatisticsSchema = z.object({
  totalApprovedResponses: statistic(),
  totalInReviewResponses: statistic(),
  totalDeniedResponses: statistic(),
  totalMissingResponses: statistic(),
  totalResponses: statistic(),
  totalQuestions: statistic(),
  totalComments: statistic(),
  approvedResponsesPercentage: statistic(),
  inReviewResponsesPercentage: statistic(),
  deniedResponsesPercentage: statistic(),
  missingResponsesPercentage: statistic(),
  responsesPercentage: statistic(),
});

export type QuestionnaireRequestAnswerStatistics = z.infer<
  typeof QuestionnaireRequestAnswerStatisticsSchema
>;

/**
 * One question of an answer request: the builder question plus the responder's
 * saved answer.
 *
 * The question half is byte-for-byte the builder shape — the legacy service ran
 * the same `buildQuestion` transformer over both reads — so this extends
 * `QuestionnaireBuilderQuestionSchema` rather than restating it, and stays
 * `looseObject` for the same reason: the parsed `meta` blob is spread flat onto
 * the question and both frontends read the flattened names.
 *
 * `answer` is a **JSON string**, not an object. The legacy `buildAnswer`
 * returned either the untouched column or a re-`JSON.stringify`'d blob with
 * signed file URLs patched into it, and frontend-v2's `parseAnswer` parses it.
 *
 * The response half is `null` for a question the responder has not answered:
 * the query LEFT JOINs the responses table, so every question of the
 * questionnaire comes back whether or not a row exists for this request.
 */
export const QuestionnaireRequestDetailQuestionSchema =
  QuestionnaireBuilderQuestionSchema.extend({
    answer: z.string().nullable().optional(),
    /** Per-answer review status. `null` until a response row exists. */
    status: z.string().nullable().optional(),
    /** `0`/`1` — the responder marked the question as not applicable. */
    isNotAvailable: z.number().nullable().optional(),
    /** A real `boolean` column, unlike its `0`/`1` neighbours. */
    isVerified: z.boolean().nullable().optional(),
    verificationMeta: z.unknown().optional(),
    autoAnswered: z.number().nullable().optional(),
    responseId: z.string().nullable().optional(),
    requestId: z.string().nullable().optional(),
    questionId: z.string().nullable().optional(),
    retracedTemplateId: z.string().nullable().optional(),
    commentCount: z.number().nullable().optional(),
  });

export type QuestionnaireRequestDetailQuestion = z.infer<
  typeof QuestionnaireRequestDetailQuestionSchema
>;

/** A company as the detail nests it — id plus its `ABOUT` profile display name. */
export const QuestionnaireRequestCompanySchema = z.object({
  id: z.string().nullable().optional(),
  name: z.string().nullable().optional(),
});

export type QuestionnaireRequestCompany = z.infer<
  typeof QuestionnaireRequestCompanySchema
>;

/**
 * `GET /api/questionnaires/requests/:requestId` — one answer request merged with
 * the questionnaire it answers.
 *
 * The merge order is inherited verbatim from the legacy service and is
 * load-bearing:
 *
 * - `id` is the **questionnaire's** id. The request's own id is
 *   `questionnaireRequestId`. frontend-v2's `normalizeAnswerRequest` documents
 *   this and takes the request id from the URL param instead.
 * - `status` is the **questionnaire's** status (`DRAFT` / `ACTIVE` / …).
 *   The request's is `requestStatus` (`OPEN` / `IN_REVIEW` / …).
 * - `dueDate` is the **questionnaire's**, not the request's.
 * - `createdAt`, `updatedAt`, `updatedByUserName`, `statistics` and `isRemoved`
 *   are the **request's** — they overwrite the questionnaire's values in the
 *   spread.
 * - `updateByUserName` (no `d`) is the questionnaire's updater, carried under
 *   the legacy's misspelt key. Both it and `updatedByUserName` are on the wire
 *   today, so both stay.
 *
 * `looseObject` because the legacy response schema was
 * `additionalProperties: true` at the top level; only `statistics` was closed.
 *
 * Date columns are deliberately left undeclared so they pass through as `Date`
 * and serialise to ISO strings — the convention `CreatedQuestionnaireSchema`
 * sets for this module.
 */
export const QuestionnaireRequestDetailSchema = z.looseObject({
  // ── The questionnaire half (the legacy `questionnaireDetailsWithResponse`
  //    projection — nine scalars plus the content).
  id: z.string(),
  name: z.string().nullable().optional(),
  description: z.string().nullable().optional(),
  companyId: z.string().nullable().optional(),
  status: z.string().nullable().optional(),
  isRetraced: z.string().nullable().optional(),
  /** The `0`/`1` the column stores, as the legacy wire carried it. */
  isTemplate: z.number().nullable().optional(),
  /** The questionnaire's updater, under the legacy's misspelt key. */
  updateByUserName: z.string().nullable().optional(),
  /**
   * The questionnaire is archived. Only the requesting company can read an
   * archived request, and only read-only: every write on it answers 409
   * `questionnaire_archived`, so the frontends hide or disable the actions.
   */
  isArchived: z.boolean(),
  sections: z.array(QuestionnaireBuilderSectionSchema),
  questions: z.array(QuestionnaireRequestDetailQuestionSchema),

  // ── The request half.
  questionnaireId: z.string(),
  questionnaireRequestId: z.string(),
  forCompanyId: z.string().nullable().optional(),
  onBehalfOfCompanyId: z.string().nullable().optional(),
  requestStatus: z.string().nullable().optional(),
  updatedByUserName: z.string().nullable().optional(),
  statistics: QuestionnaireRequestAnswerStatisticsSchema.nullable().optional(),
  /** `0`/`1` — the request was soft-deleted. Read but never filtered on. */
  isRemoved: z.number().nullable().optional(),

  // ── Company context.
  requestCompany: QuestionnaireRequestCompanySchema,
  onBehalfOfCompany: QuestionnaireRequestCompanySchema,
  requestedCompany: QuestionnaireRequestCompanySchema,

  /** Whether the requesting company is linked to the on-behalf-of company. */
  supplierConnected: z.boolean(),
  /** Whether the requesting company is linked to the answering company. */
  answeredByConnected: z.boolean(),
});

export type QuestionnaireRequestDetail = z.infer<
  typeof QuestionnaireRequestDetailSchema
>;
